import React, { useEffect, useState } from 'react';
import { sessionUsageService, MySessionUsage } from '../../services/api/sessionUsageService';
import { getSessionLimit } from '../../utils/entitlements';
import { colors } from '../../theme/colors';
import { useI18n } from '../../i18n';

/**
 * Compact session-usage counter for the consult header (medical officers).
 *
 * Inline styles only, no stylesheet import: mounting a `.scss`-importing
 * component inside `Header` injected a CSS module into the Header chunk and
 * triggered a mini-css-extract "Conflicting order" build error. Keeping this
 * counter styleless avoids that while still showing `used / limit`.
 */
export const SessionsCounter: React.FC<{
  plain?: boolean;
  onlyWhenNearLimit?: boolean;
  showBar?: boolean;
}> = ({ plain = false, onlyWhenNearLimit = false, showBar = false }) => {
  const { t } = useI18n();
  const [usage, setUsage] = useState<MySessionUsage | null>(null);
  const fallbackLimit = getSessionLimit();

  useEffect(() => {
    let cancelled = false;
    sessionUsageService
      .getMySessionUsage()
      .then((d) => {
        if (!cancelled) setUsage(d);
      })
      .catch(() => {
        /* header counter is best-effort — stay silent on failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const used = usage?.used ?? 0;
  const limit = usage ? usage.limit : fallbackLimit;
  const isUnlimited = limit === null || limit === undefined;
  const util = !isUnlimited && (limit as number) > 0 ? (used / (limit as number)) * 100 : 0;

  const tone = isUnlimited
    ? colors.textSecondary
    : util >= 100
      ? colors.errorText
      : util >= 90
        ? colors.warningText
        : colors.textSecondary;

  if (onlyWhenNearLimit && (isUnlimited || util < 80)) return null;

  const title = isUnlimited
    ? t('usage.unlimitedTitle')
    : t('usage.usedTitle', { used, limit: limit as number });

  return (
    <div
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: plain ? 0 : '4px 10px',
        borderRadius: plain ? 0 : 9999,
        border: plain ? 'none' : `1px solid ${colors.border}`,
        background: plain ? 'transparent' : colors.backgroundPaper,
        fontSize: '0.78rem',
        fontWeight: plain ? 500 : 600,
        color: tone,
        whiteSpace: 'nowrap',
      }}
    >
      {isUnlimited ? (
        <span>{t('usage.unlimitedChip')}</span>
      ) : (
        <>
          <span>{used}/{limit}</span>
          {showBar ? (
            <span
              role="progressbar"
              aria-valuenow={used}
              aria-valuemin={0}
              aria-valuemax={limit as number}
              aria-label={title}
              style={{
                display: 'inline-block',
                width: 56,
                height: 4,
                borderRadius: 9999,
                background: colors.border,
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: `${Math.min(util, 100)}%`,
                  height: '100%',
                  borderRadius: 9999,
                  background: tone,
                }}
              />
            </span>
          ) : (
            <span style={{ color: colors.textMuted, fontWeight: 500 }}>{t('usage.sessionsLabel')}</span>
          )}
        </>
      )}
    </div>
  );
};
